import { useState, useEffect, useMemo } from "react";
import { Card, Button, Form, Upload, Table, Tag, message, Modal, Alert, Empty, Spin, Input, Checkbox, Popconfirm } from "antd";
import { 
    UploadOutlined, 
    FontSizeOutlined,
    ReloadOutlined,
    InboxOutlined,
    SearchOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { getFonts, uploadFont, deleteFontsBulk, reprocessFontNames } from "../../core/api/pdfParser";
import useGlobalFilters from "../../hooks/useGlobalFilters";

const { Dragger } = Upload;

function FontManager() {
    const { brandIds, hasSelectedBrands } = useGlobalFilters();
    const currentBrandId = brandIds.length > 0 ? brandIds[0] : null;
    
    const [fonts, setFonts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const fetchFonts = async () => {
        setLoading(true);
        try {
            const result = await getFonts(currentBrandId);
            setFonts(result.fonts || []);
        } catch (err) {
            message.error("Failed to fetch fonts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFonts();
    }, [currentBrandId]);

    const filteredFonts = useMemo(() => {
        if (!searchText.trim()) return fonts;
        const q = searchText.toLowerCase();
        return fonts.filter(f =>
            (f.name || "").toLowerCase().includes(q) ||
            (f.font_family || "").toLowerCase().includes(q) ||
            (f.filename || "").toLowerCase().includes(q)
        );
    }, [fonts, searchText]);

    const allFilteredSelected = filteredFonts.length > 0 && filteredFonts.every(f => selectedIds.includes(f.id));
    const someFilteredSelected = filteredFonts.some(f => selectedIds.includes(f.id));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            const filteredIdSet = new Set(filteredFonts.map(f => f.id));
            setSelectedIds(prev => prev.filter(id => !filteredIdSet.has(id)));
        } else {
            const filteredIds = filteredFonts.map(f => f.id);
            setSelectedIds(prev => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setDeleting(true);
        try {
            const result = await deleteFontsBulk(selectedIds);
            message.success(`Deleted ${result.deleted} font${result.deleted !== 1 ? 's' : ''}`);
            setSelectedIds([]);
            fetchFonts();
        } catch (err) {
            message.error("Failed to delete fonts");
        } finally {
            setDeleting(false);
        }
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning("Please select at least one font file");
            return;
        }

        setUploading(true);
        let successCount = 0;
        let failCount = 0;
        try {
            for (const item of fileList) {
                const file = item.originFileObj || item;
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
                try {
                    await uploadFont(file, nameWithoutExt, nameWithoutExt, currentBrandId);
                    successCount++;
                } catch (err) {
                    failCount++;
                    console.error(`Failed to upload ${file.name}:`, err);
                }
            }
            if (successCount > 0) {
                message.success(`${successCount} font${successCount > 1 ? 's' : ''} uploaded successfully${failCount > 0 ? ` (${failCount} failed)` : ''}`);
            } else {
                message.error("All font uploads failed");
            }
            setUploadModalVisible(false);
            form.resetFields();
            setFileList([]);
            fetchFonts();
        } catch (err) {
            message.error(err.response?.data?.error || "Failed to upload fonts");
        } finally {
            setUploading(false);
        }
    };

    const columns = [
        {
            title: (
                <Checkbox
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected && !allFilteredSelected}
                    onChange={toggleSelectAll}
                />
            ),
            key: "select",
            width: 48,
            render: (_, record) => (
                <Checkbox
                    checked={selectedIds.includes(record.id)}
                    onChange={() => toggleSelect(record.id)}
                />
            ),
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (name, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: record.is_default ? "#1a1a2e" : "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        <FontSizeOutlined style={{ 
                            color: record.is_default ? "#fff" : "#1a1a2e", 
                            fontSize: 16 
                        }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 500 }}>{name || record.filename?.replace(/\.[^/.]+$/, '')}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{record.filename}</div>
                    </div>
                </div>
            ),
        },
        {
            title: "Font Family",
            dataIndex: "font_family",
            key: "font_family",
            render: (family) => (
                <span style={{ fontFamily: "monospace" }}>{family || "—"}</span>
            ),
        },
        {
            title: "Type",
            dataIndex: "font_type",
            key: "font_type",
            width: 100,
            render: (type) => (
                <Tag>{type?.toUpperCase() || "UNKNOWN"}</Tag>
            ),
        },
        {
            title: "Status",
            key: "status",
            width: 120,
            render: (_, record) => (
                <div>
                    {record.is_default && (
                        <Tag color="blue">Default</Tag>
                    )}
                    <Tag color={record.status === "active" ? "success" : "default"}>
                        {record.status}
                    </Tag>
                </div>
            ),
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (date) => new Date(date).toLocaleDateString(),
        },
    ];

    const uploadProps = {
        beforeUpload: () => false,
        accept: ".ttf,.otf,.woff,.woff2",
        multiple: true,
        fileList,
        onChange: ({ fileList: newFileList }) => {
            setFileList(newFileList);
        },
    };

    if (!hasSelectedBrands) {
        return (
            <Card style={{ textAlign: "center", padding: 60 }}>
                <Empty 
                    description="Please select a brand to manage fonts"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ margin: 0, fontWeight: 600 }}>
                    Font Library ({fonts.length})
                </h5>
                <div style={{ display: "flex", gap: 8 }}>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchFonts}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setUploadModalVisible(true)}
                        style={{ background: "#1a1a2e" }}
                    >
                        Upload Font
                    </Button>
                </div>
            </div>

            <Alert
                message="Font Usage"
                description={
                    <span>
                        Upload custom fonts (TTF, OTF, WOFF) to match your PDF template&apos;s typography. 
                        When generating PDFs, fonts will be automatically matched by family name, 
                        or you can manually map fonts to placeholders in the Generate tab.
                    </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            {loading && fonts.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : fonts.length === 0 ? (
                <Card style={{ textAlign: "center", padding: 40 }}>
                    <Empty 
                        description="No custom fonts uploaded yet"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <Button 
                            type="primary" 
                            icon={<UploadOutlined />}
                            onClick={() => setUploadModalVisible(true)}
                            style={{ marginTop: 16 }}
                        >
                            Upload Your First Font
                        </Button>
                    </Empty>
                </Card>
            ) : (
                <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                    <div style={{ padding: '16px 16px 0', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                            placeholder="Search fonts..."
                            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            style={{ maxWidth: 300 }}
                        />
                        {selectedIds.length > 0 && (
                            <Popconfirm
                                title={`Delete ${selectedIds.length} font${selectedIds.length !== 1 ? 's' : ''}?`}
                                description="This action cannot be undone."
                                onConfirm={handleBulkDelete}
                                okText="Delete"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    loading={deleting}
                                >
                                    Delete Selected ({selectedIds.length})
                                </Button>
                            </Popconfirm>
                        )}
                        {searchText && (
                            <span style={{ fontSize: 12, color: '#888' }}>
                                Showing {filteredFonts.length} of {fonts.length}
                            </span>
                        )}
                    </div>
                    <Table
                        dataSource={filteredFonts}
                        columns={columns}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        style={{ marginTop: 8 }}
                    />
                </Card>
            )}

            {/* Upload Modal */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: "#1a1a2e",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <FontSizeOutlined style={{ color: "#fff", fontSize: 20 }} />
                        </div>
                        <span style={{ fontWeight: 600 }}>Upload Font</span>
                    </div>
                }
                open={uploadModalVisible}
                zIndex={100000}
                onCancel={() => {
                    setUploadModalVisible(false);
                    form.resetFields();
                    setFileList([]);
                }}
                footer={[
                    <Button 
                        key="cancel" 
                        onClick={() => {
                            setUploadModalVisible(false);
                            form.resetFields();
                            setFileList([]);
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="upload"
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleUpload}
                        loading={uploading}
                        disabled={fileList.length === 0}
                        style={{ background: "#1a1a2e" }}
                    >
                        Upload {fileList.length > 1 ? `${fileList.length} Fonts` : 'Font'}
                    </Button>,
                ]}
                width={500}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item label="Font Files">
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#1a1a2e" }} />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag font files to upload
                            </p>
                            <p className="ant-upload-hint">
                                Supports TTF, OTF, WOFF, and WOFF2 — select multiple files at once
                            </p>
                        </Dragger>
                    </Form.Item>
                    {fileList.length > 1 && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: -8, marginBottom: 12 }}>
                            {fileList.length} fonts selected — names will be auto-filled from filenames
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
}

export default FontManager;
